import {
    FC,
    ReactElement,
    useEffect,
    useMemo,
    useState,
} from 'react';

import { ProductCard } from '../../containers';
import { Product } from '../../entities/@products/Product/Product';
import { GTMEvent } from '../../entities/GTM/GTM';
import { generateGTMProduct } from '../../entities/GTM/GTMRequests';
import { sendGTMEvent } from '../../entities/GTM/GTMService';
import { addProductVariantToCart } from '../../redux/@shop/cart/cartActions';
import { addProductVariantToWishlist, removeProductVariantFromWishlist } from '../../redux/@shop/wishlist/wishlistActions';
import { useTypedDispatch, useTypedSelector } from '../../redux/store';

interface ConnectedProductCardProps {
    isGridView?: boolean;
    product: Product;
    className?: string;
    pictureWrapperClassName?: string;
    pictureClassName?: string;
    secondaryImageClassName?: string;
}

export const ConnectedProductCard: FC<ConnectedProductCardProps> = ({
    isGridView,
    product,
    className = '',
    pictureWrapperClassName = '',
    pictureClassName = '',
    secondaryImageClassName = '',
}): ReactElement => {
    const dispatch = useTypedDispatch();

    const cartIsLoading = useTypedSelector(state => state.cartSlice.isLoading);
    const cart = useTypedSelector(state => state.cartSlice.cart);

    const wishlistIsLoading = useTypedSelector(state => state.wishlistSlice.isLoading);
    const wishlist = useTypedSelector(state => state.wishlistSlice.wishlist);

    const inCartQuantity = useMemo((): number => {
        const foundItem = cart.items.find(item => item.variantCode === product?.variantCode);

        return foundItem?.quantity || 0;
    }, [cart]);

    const [isInWishlist, setIsInWishlist] = useState<boolean>(false);

    const gtmProduct = generateGTMProduct(product);

    useEffect((): void => {
        const isProductVariantInWishlist = wishlist.variantIds.includes(product.id);

        setIsInWishlist(isProductVariantInWishlist);
    }, [wishlist]);

    const handleAddToCart = async (): Promise<void> => {
        await dispatch(addProductVariantToCart(product.variantCode));

        sendGTMEvent(GTMEvent.addToCart, gtmProduct);
    };

    const handleWishlistButtonClick = async (): Promise<void> => {
        if (isInWishlist) {
            await dispatch(removeProductVariantFromWishlist(product.variantId));
            return;
        }

        await dispatch(addProductVariantToWishlist(product.variantId));

        sendGTMEvent(GTMEvent.addToWishlist, gtmProduct);
    };

    return (
        <ProductCard
            cartIsLoading={cartIsLoading}
            wishlistIsLoading={wishlistIsLoading}
            isGridView={isGridView}
            isInWishlist={isInWishlist}
            product={product}
            onAddToCart={handleAddToCart}
            onClickWishlistButton={handleWishlistButtonClick}
            inCartQuantity={inCartQuantity}
            className={className}
            pictureWrapperClassName={pictureWrapperClassName}
            pictureClassName={pictureClassName}
            secondaryImageClassName={secondaryImageClassName}
        />
    );
};
